/**
 * The form for a reassurance-guard case: the chat (it ends with the user message the guard
 * classifies), the user's OCD profile with only the keys the app writes, and the gold
 * annotation the label and the judge are graded against.
 */
import { useState } from 'react';
import { ConversationEditor, toConversation, toLines, type Line } from './ConversationEditor';
import { FieldLabel, ListField, fieldClass, isRecord, strings, toList, type ContentEditorProps } from './editorParts';

/** The label sets the API accepts: one label, or one with maybe. `value` is the labels joined with commas. */
const LABEL_SETS: { value: string; text: string }[] = [
  { value: 'yes', text: 'yes' },
  { value: 'no', text: 'no' },
  { value: 'maybe', text: 'maybe' },
  { value: 'yes,maybe', text: 'yes or maybe' },
  { value: 'no,maybe', text: 'no or maybe' },
];

interface ProfileEntry {
  userProvided: string;
  inferred: string;
}

interface Draft {
  lines: Line[];
  /** By ocd_profile key. A key can be missing when the suite's key list grows. */
  profile: Partial<Record<string, ProfileEntry>>;
  learningQuestions: string;
  labelSet: string;
  fearedConsequence: string;
  decisiveSignals: string;
  misleadingSignals: string;
  mitigators: string;
  missingInfo: string;
  whyTwinDiffers: string;
  clinicalRationale: string;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

function toDraft(input: unknown, expect: unknown, profileKeys: string[]): Draft {
  const source = isRecord(input) ? input : {};
  const profile = isRecord(source.ocdProfile) ? source.ocdProfile : {};
  const infos = isRecord(profile.ocdInfos) ? profile.ocdInfos : {};
  const questions = Array.isArray(profile.learningQuestions) ? profile.learningQuestions.filter(isRecord) : [];
  const expected = isRecord(expect) ? expect : {};
  const labels = strings(expected.labels).join(',');

  return {
    lines: toLines(input),
    profile: Object.fromEntries(
      profileKeys.map((key) => {
        const raw = infos[key];
        const info = isRecord(raw) ? raw : {};
        return [key, { userProvided: strings(info.userProvided).join('\n'), inferred: strings(info.inferred).join('\n') }];
      }),
    ),
    learningQuestions: questions.length > 0 ? JSON.stringify(questions.map((q) => ({ question: q.question, answers: q.answers })), null, 2) : '',
    labelSet: LABEL_SETS.some((set) => set.value === labels) ? labels : 'yes',
    fearedConsequence: text(expected.fearedConsequence),
    decisiveSignals: strings(expected.decisiveSignals).join('\n'),
    misleadingSignals: strings(expected.misleadingSignals).join('\n'),
    mitigators: strings(expected.mitigators).join('\n'),
    missingInfo: strings(expected.missingInfo).join('\n'),
    whyTwinDiffers: text(expected.whyTwinDiffers),
    clinicalRationale: text(expected.clinicalRationale),
  };
}

/** The learning questions as the API wants them, with IDs by position, or a problem. */
function parseLearningQuestions(draftText: string): { value: Record<string, unknown>[] } | { problem: string } {
  if (draftText.trim() === '') return { value: [] };
  try {
    const parsed: unknown = JSON.parse(draftText);
    if (!Array.isArray(parsed)) return { problem: 'Learning questions must be a JSON array.' };
    return { value: parsed.map((entry: unknown, index) => ({ id: `lq_${index + 1}`, ...(isRecord(entry) ? entry : {}) })) };
  } catch (caught) {
    return { problem: `Learning questions: ${caught instanceof Error ? caught.message : 'not valid JSON.'}` };
  }
}

/** The case content, or a problem that stops it being sent. The API checks the rest. */
function fromDraft(draft: Draft, profileKeys: string[]): { input: unknown; expect: unknown } | { problem: string } {
  const questions = parseLearningQuestions(draft.learningQuestions);
  if ('problem' in questions) return questions;

  const ocdInfos = Object.fromEntries(
    profileKeys.flatMap((key) => {
      const entry = draft.profile[key];
      const userProvided = toList(entry?.userProvided ?? '');
      const inferred = toList(entry?.inferred ?? '');
      return userProvided.length > 0 || inferred.length > 0 ? [[key, { id: key, userProvided, inferred }]] : [];
    }),
  );
  const hasProfile = Object.keys(ocdInfos).length > 0 || questions.value.length > 0;

  const labels = draft.labelSet.split(',');
  const hasMaybe = labels.includes('maybe');
  const misleading = toList(draft.misleadingSignals);
  const mitigators = toList(draft.mitigators);
  const missing = toList(draft.missingInfo);

  return {
    input: {
      conversation: toConversation(draft.lines),
      ...(hasProfile && { ocdProfile: { ocdInfos, learningQuestions: questions.value } }),
    },
    expect: {
      labels,
      fearedConsequence: draft.fearedConsequence.trim() === '' ? null : draft.fearedConsequence.trim(),
      decisiveSignals: toList(draft.decisiveSignals),
      ...(misleading.length > 0 && { misleadingSignals: misleading }),
      ...(mitigators.length > 0 && { mitigators }),
      ...(hasMaybe && missing.length > 0 && { missingInfo: missing }),
      ...(draft.whyTwinDiffers.trim() !== '' && { whyTwinDiffers: draft.whyTwinDiffers.trim() }),
      clinicalRationale: draft.clinicalRationale.trim(),
    },
  };
}

const smallField = 'px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-900 disabled:bg-gray-50';

const listRows = (value: string) => Math.max(2, value.split('\n').length + 1);

export const ReassuranceGuardContentEditor = ({ initial, options, disabled, onChange }: ContentEditorProps) => {
  // The ocd_profile keys the app writes, from the API (caseSchema.ts GUARD_PROFILE_KEYS).
  const profileKeys = options.profileKeys ?? [];
  const [draft, setDraft] = useState<Draft>(() => toDraft(initial?.input, initial?.expect, profileKeys));
  const [problem, setProblem] = useState<string | null>(null);

  const update = (change: Partial<Draft>) => {
    const next = { ...draft, ...change };
    setDraft(next);
    const result = fromDraft(next, profileKeys);
    if ('problem' in result) {
      setProblem(result.problem);
      onChange(null);
    } else {
      setProblem(null);
      onChange(result);
    }
  };

  const setProfile = (key: string, change: Partial<ProfileEntry>) =>
    update({ profile: { ...draft.profile, [key]: { userProvided: '', inferred: '', ...draft.profile[key], ...change } } });

  const hasMaybe = draft.labelSet.includes('maybe');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6">
      <div className="space-y-4">
        <ConversationEditor lines={draft.lines} hint="Ends with the user message the guard classifies." disabled={disabled} onChange={(lines) => update({ lines })} />

        <div>
          <FieldLabel title="OCD profile" hint="One value per line. Leave all empty for a user with no profile" />
          <div className="space-y-2">
            {profileKeys.map((key) => (
              <div key={key} className="grid grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)] gap-2 items-start">
                <span className="font-mono text-xs text-gray-700 pt-2 break-all">{key}</span>
                <textarea
                  value={draft.profile[key]?.userProvided ?? ''}
                  disabled={disabled}
                  rows={listRows(draft.profile[key]?.userProvided ?? '')}
                  placeholder="user provided"
                  onChange={(event) => setProfile(key, { userProvided: event.target.value })}
                  className={fieldClass}
                />
                <textarea
                  value={draft.profile[key]?.inferred ?? ''}
                  disabled={disabled}
                  rows={listRows(draft.profile[key]?.inferred ?? '')}
                  placeholder="inferred"
                  onChange={(event) => setProfile(key, { inferred: event.target.value })}
                  className={fieldClass}
                />
              </div>
            ))}
          </div>
        </div>

        <label className="block">
          <FieldLabel title="Learning questions" hint='Optional JSON: [{ "question": "...", "answers": ["..."] }]' />
          <textarea
            value={draft.learningQuestions}
            disabled={disabled}
            rows={draft.learningQuestions ? 6 : 2}
            onChange={(event) => update({ learningQuestions: event.target.value })}
            className={`${fieldClass} font-mono text-xs`}
          />
        </label>
        {problem && <p className="text-sm text-red-700">{problem}</p>}
      </div>

      <div className="space-y-4">
        <label className="block">
          <FieldLabel title="Expected label" hint="Graded in code" />
          <select value={draft.labelSet} disabled={disabled} onChange={(event) => update({ labelSet: event.target.value })} className={smallField}>
            {LABEL_SETS.map((set) => (
              <option key={set.value} value={set.value}>
                {set.text}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <FieldLabel title="Feared consequence" hint="Empty: none applies" />
          <input type="text" value={draft.fearedConsequence} disabled={disabled} onChange={(event) => update({ fearedConsequence: event.target.value })} className={fieldClass} />
        </label>

        <p className="text-xs text-gray-600">One item per line.</p>
        <ListField title="Decisive signals" hint="1-3. The reasoning must rely on one" value={draft.decisiveSignals} disabled={disabled} onChange={(decisiveSignals) => update({ decisiveSignals })} />
        <ListField title="Misleading signals" hint="0-3. The label must not rest on them" value={draft.misleadingSignals} disabled={disabled} onChange={(misleadingSignals) => update({ misleadingSignals })} />
        <ListField title="Mitigators" hint="0-3. The reasoning must weigh each" value={draft.mitigators} disabled={disabled} onChange={(mitigators) => update({ mitigators })} />
        {hasMaybe && (
          <ListField title="Missing information" hint="1-3. What is missing to decide" value={draft.missingInfo} disabled={disabled} onChange={(missingInfo) => update({ missingInfo })} />
        )}

        <label className="block">
          <FieldLabel title="Why the twin differs" hint="Optional, for a minimal pair" />
          <textarea value={draft.whyTwinDiffers} disabled={disabled} rows={2} onChange={(event) => update({ whyTwinDiffers: event.target.value })} className={fieldClass} />
        </label>

        <label className="block">
          <FieldLabel title="Clinical rationale" hint="1-2 sentences. Context for the judge" />
          <textarea value={draft.clinicalRationale} disabled={disabled} rows={3} onChange={(event) => update({ clinicalRationale: event.target.value })} className={fieldClass} />
        </label>
      </div>
    </div>
  );
};
